import { CollectionResponse } from 'api/product';
import { getSmartCollection } from 'api/product/getSmartColleciton';
import { SmartCollectionResponse } from 'api/product/getSmartColleciton/types';
import { isSmartCollectionResponse } from 'api/product/getSmartColleciton/utils';
import cn from 'classnames';
import Headline from 'components/headline';
import { fetchContentfulByHandle } from 'connectors/contentful';
import { Entry } from 'contentful';
import { IProductPagesFields } from 'global';
import { CollectionPropTypes } from 'modules/collection/types/CollectionPropTypes';
import { useMemo } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import { generateInputFromSegmentation } from 'utils/api/smartCollection';
import { CollectionContext, CollectionProvider } from '../../state';
import ProductsGrid from '../products-grid';
import ResultCounter from '../result-counter';

interface StandaloneCDPPropsBase {
  bestsellerSectionClassName?: string;
  children?: React.ReactNode;
  className?: string;
  customButton?: React.ReactNode;
  productsLimit?: number;
  showFilter?: boolean;
  showMoreButton?: boolean;
  subTitle?: string;
  swrConfig?: SWRConfiguration;
  title: string;
  trackingElementName?: string;
  /** If true, best seller ribbon is not shown */
  omitBestSeller?: boolean;
  /** Name given to impression tracker via collection context */
  trackingListName?: string;
  /** Helper to propagate tags to parent component  */
  setUsedTags?: React.Dispatch<React.SetStateAction<string[]>>;
}

type StandaloneCDPProps = CollectionPropTypes<StandaloneCDPPropsBase>;

/**
 * Component that wrap a products grid with a collection provider and the logic to fetch the collection data
 * and the related contentful data based on the collection handle.
 * Children are rendered before the products grid and className is applied to the root div.
 */
const StandaloneCDP: React.FC<StandaloneCDPProps> = ({
  bestsellerSectionClassName,
  children,
  className,
  collectionHandle,
  customButton,
  productsLimit,
  showFilter = true,
  showMoreButton,
  subTitle,
  swrConfig = {},
  title,
  trackingElementName,
  segmentation,
  smartCollectionInput,
  omitBestSeller = false,
  trackingListName,
  setUsedTags,
}) => {
  const contentfulUrl = `collections/${collectionHandle}`;

  const { data, error } = useSWR<{
    collection: CollectionResponse | SmartCollectionResponse;
    contentful: Entry<IProductPagesFields> | null;
  }>(
    [collectionHandle, contentfulUrl, segmentation, smartCollectionInput],
    (collectionUrl, contentfulUrl, segmentation) => {
      if (!collectionHandle && !segmentation && !smartCollectionInput) {
        throw new Error('no handle and segmentation is assigned');
      }
      let collectionPromise;
      if (smartCollectionInput) {
        collectionPromise = getSmartCollection(smartCollectionInput);
      } else if (collectionUrl) {
        collectionPromise = getSmartCollection(collectionUrl, true);
      } else {
        collectionPromise = getSmartCollection(
          generateInputFromSegmentation({ segmentation })
        );
      }

      return Promise.all([
        collectionPromise,
        fetchContentfulByHandle(contentfulUrl),
      ]).then(([collection, contentful]) => {
        return {
          collection,
          contentful,
        };
      });
    },
    { revalidateOnFocus: false, ...swrConfig }
  );

  // If the collection is dynamic, the headline is generated by the backend and therfore we
  // use this data instead of the props
  const headlineData = useMemo(() => {
    const { collection } = data ?? {};
    const isDynamic = collection && isSmartCollectionResponse(collection);

    return {
      title: isDynamic ? collection.title : title,
      subTitle: isDynamic ? undefined : subTitle,
    };
  }, [data, title, subTitle]);

  const providerProps = useMemo(() => {
    if (collectionHandle) {
      return {
        collectionHandle,
      };
    } else if (segmentation) {
      return {
        segmentation,
      };
    } else if (smartCollectionInput) {
      return { smartCollectionInput };
    }
  }, [collectionHandle, smartCollectionInput, segmentation]);

  if (error || !data?.collection || !providerProps) return null;

  return (
    <CollectionProvider
      {...providerProps}
      // Tags is not used in standalone CDP
      tags={[]}
      collection={data.collection}
      trackingListName={trackingListName}
      setUsedTags={setUsedTags}
    >
      <div className={cn(className)}>
        {children}
        <Headline
          classNames="lg:mb-5 mb-1.5"
          shouldBeH1={false}
          subTitle={headlineData.subTitle}
          title={headlineData.title}
          extraElement={
            <span className="hidden lg:block">
              <CollectionContext.Consumer>
                {(val) => (
                  <ResultCounter productCount={val.visibleProducts.length} />
                )}
              </CollectionContext.Consumer>
            </span>
          }
        />
        <ProductsGrid
          bestsellerSectionClassName={bestsellerSectionClassName}
          contentfulData={data.contentful}
          customButton={customButton}
          productsLimit={productsLimit}
          showFilter={showFilter}
          showMoreButton={showMoreButton}
          trackingElementName={trackingElementName}
          omitBestSeller={omitBestSeller}
        />
      </div>
    </CollectionProvider>
  );
};

export default StandaloneCDP;
